import { Box, Stack } from "@dropbox/dig-foundations";
import useDropboxAccount from "@mirage/service-auth/useDropboxAccount";
import useConnectors from "@mirage/service-connectors/useConnectors";
import {
  areFiltersSet,
  isSupportedActivityFeedConnector,
  useSupportedFeedConnectorNames,
} from "@mirage/service-feed/util/helpers";
import { meticulousRedactedStringClass } from "@mirage/translations";
import { ActivityListEmptyState } from "../ActivityListEmptyState/ActivityListEmptyState";
import { ActivityListEmptyStateAddApps } from "../ActivityListEmptyState/ActivityListEmptyStateAddApps";
import { ActivityListEmptyStateResetFilters } from "../ActivityListEmptyState/ActivityListEmptyStateResetFilters";
import { InfiniteScroll } from "../InfiniteScroll/InfiniteScroll";
import { ActivityListItem, ActivityListItemSkeleton } from "./ActivityListItem";
import { NoMoreActivityItems } from "./NoMoreActivityItems";

import type { dash_feed, stacks } from "@dropbox/api-v2-client";
import { PossibleTabContent } from "@mirage/feed/hooks/useOnInViewTrigger";
import type { ActivityFeedFilters } from "@mirage/service-feed/types";
import { useLastViewedStackInfo } from "@mirage/service-recent-content/hooks/useLastViewedStackInfo";
import { usePublishedContents } from "@mirage/stacks/useInitPublishContentsData";
import { useMemo } from "react";

type ActivityListProps = {
  activities: dash_feed.FeedItem[];
  clearFilters: () => void;
  filters: ActivityFeedFilters;
  hasMore: boolean;
  onViewItem: (activity: PossibleTabContent, position: number) => void;
  onFeedScroll?: (activity: PossibleTabContent, position: number) => void;
  onReachEnd?: () => void;
  showLoadingPlaceholders: boolean;
};

const NUM_LOADER_ITEMS = 10;

export const ActivityList = ({
  activities,
  clearFilters,
  filters,
  hasMore,
  onViewItem,
  onFeedScroll,
  onReachEnd,
  showLoadingPlaceholders,
}: ActivityListProps) => {
  const useInfiniteScroll = !!onReachEnd && hasMore && !showLoadingPlaceholders;
  const currentAccount = useDropboxAccount();
  const showEndOfFeed = !hasMore && !showLoadingPlaceholders;
  const { connectors } = useConnectors();
  const supportedConnectorNames = useSupportedFeedConnectorNames();
  const activeFeedConnectors = connectors.filter((connector) =>
    isSupportedActivityFeedConnector(connector, supportedConnectorNames),
  );
  const lastViewedInfo = useLastViewedStackInfo();
  const publishedContents = usePublishedContents();
  const companyStackInfo = useMemo(
    () =>
      (publishedContents || []).reduce(
        (acc, content) => {
          if (
            content[".tag"] === "stack" &&
            content.namespace_id &&
            content.publish_data
          ) {
            acc[content.namespace_id] = content.publish_data;
          }
          return acc;
        },
        {} as Record<string, stacks.PublishData>,
      ),
    [publishedContents],
  );

  if (activities.length === 0 && !showLoadingPlaceholders) {
    if (activeFeedConnectors.length === 0) {
      return <ActivityListEmptyStateAddApps />;
    } else if (areFiltersSet(filters)) {
      return <ActivityListEmptyStateResetFilters clearFilters={clearFilters} />;
    } else {
      return <ActivityListEmptyState />;
    }
  }

  return (
    <Box>
      <Stack gap="Micro Small">
        {activities.map((activity, index) => (
          <ActivityListItem
            key={index}
            activity={activity}
            currentAccount={currentAccount}
            index={index}
            onInView={onViewItem}
            onScrollIntoView={onFeedScroll}
            className={meticulousRedactedStringClass}
            stackLastViewedInfo={lastViewedInfo}
            companyStackInfo={companyStackInfo}
          />
        ))}

        {showLoadingPlaceholders &&
          Array.from({ length: NUM_LOADER_ITEMS }).map((_, index) => (
            <ActivityListItemSkeleton key={index} />
          ))}

        {useInfiniteScroll && <InfiniteScroll onTrigger={onReachEnd} />}
      </Stack>

      {showEndOfFeed && <NoMoreActivityItems />}
    </Box>
  );
};
